import { useAppDispatch } from '~/store/appDispatch.ts';
import { useSelector } from 'react-redux';
import useGetContragentTypes from '~/modules/identification/hooks/useGetContragentTypes.ts';
import { useFinancialConditionForm } from '~/modules/identification/forms/FinancialCondition/useFinancialConditionForm.ts';
import { cloneDeep } from 'lodash';
import { updateForm } from '~/store/identification-form/thunks/thunks.ts';
import {
  IdentificationFormStepNames,
  IdentificationFormSteps,
} from '~/modules/identification/types.ts';
import { TIdentificationForm } from '~/store/identification-form/types.ts';
import { updateFormData, updateFormDataBack } from '~/store/identification-form/slices.ts';
import { InformationMessageBlock } from '~/modules/identification/components/InformationMessage/InformationMessageBlock.tsx';
import { infoMessages } from '~/constants/infoMessages';
import { Box, Button, Typography } from '@mui/material';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader';
import { FormProvider } from 'react-hook-form';
import { TFinancialCondition } from '~/modules/identification/forms/FinancialCondition/types.ts';
import { BlockFinancialSoleTrader } from '~/modules/identification/forms/FinancialCondition/components/BlockFinancialSoleTrader.tsx';
import { BlockFinancialLegalResident } from '~/modules/identification/forms/FinancialCondition/components/BlockFinancialLegalResident.tsx';
import { styles as main } from './styles.ts';
import { styles as root } from '~/modules/identification/styles.ts';
import FormAutocomplete from '~/components/FormComponents/FormAutocomplete.tsx';
import {
  getPrevDateOptions,
  normalizeZeroValues,
} from '~/modules/identification/forms/FinancialCondition/utils.ts';
import { BankAccounts } from '~/modules/identification/forms/FinancialCondition/components/BankAccounts/BankAccounts.tsx';
import { TAutocompleteOption } from '~/components/AutocompleteField/types';
import { testDataFinancialCondition, titleFinancialCondition } from './constants.ts';
import { useFeatureFlags } from '~/helpers/FeatureFlagsContext.tsx';
import { commonStyles } from '~/theme/styles/commonStyles.ts';
import {
  selectApplicationId,
  selectIsReidentification,
} from '~/store/identification-form/selectors.ts';
import { onInvalidForm } from '~/helpers/utils.ts';
import { SetDirtyModalValues } from '../../components/SetDirtyModalValues.tsx';

interface IFinancialConditionProps {
  onNextStep: () => void;
}

export default function FinancialCondition({ onNextStep }: IFinancialConditionProps) {
  const dispatch = useAppDispatch();
  const appId = useSelector(selectApplicationId);
  const isReidentification = useSelector(selectIsReidentification);
  const { isSoleTrader } = useGetContragentTypes();
  const { isTestEnvironmentEnabled } = useFeatureFlags();

  const form = useFinancialConditionForm();
  const { isSubmitting, isDirty } = form.formState;

  const handleFillTestData = () =>
    form.reset(testDataFinancialCondition, { keepDefaultValues: true });

  const prevDateOptions = getPrevDateOptions();

  const onSubmit =
    (isDirty: boolean, disableGoNext?: boolean) => async (values: TFinancialCondition) => {
      if (isDirty) {
        try {
          const normalizeZero = normalizeZeroValues(values);

          const financialStateValues = {
            ...normalizeZero,
            bankAccounts: normalizeZero.noOtherBankAccounts ? [] : normalizeZero.bankAccounts,
            prevDate: (normalizeZero.prevDate as TAutocompleteOption)?.label as string,
          } satisfies TFinancialCondition;

          const data = {
            financialState: financialStateValues,
            applicationId: appId as number,
          };
          await dispatch(updateForm({ data, reportId: IdentificationFormSteps.financialState }));
          const formData = {
            field: IdentificationFormStepNames.financialState as keyof TIdentificationForm,
            data: cloneDeep(values),
          };
          dispatch(updateFormData(formData));
          dispatch(updateFormDataBack(formData));
          !disableGoNext && onNextStep();
        } catch (error) {
          console.error(error);
        }
      } else {
        !disableGoNext && onNextStep();
      }
    };

  return (
    <FormProvider {...form}>
      <Box component="form" onSubmit={form.handleSubmit(onSubmit(isDirty), onInvalidForm)}>
        {isTestEnvironmentEnabled && (
          <Box sx={root.testEnvironmentButton}>
            <Button variant={'outlined'} onClick={handleFillTestData}>
              Заповнити даними
            </Button>
          </Box>
        )}
        <Box sx={{ ...commonStyles.centerFlexContainer, flexDirection: 'column', width: '100%' }}>
          {isReidentification && (
            <InformationMessageBlock informationText={infoMessages.RE_VERIFICATION_MESSAGE} />
          )}
          <BankAccounts />
          <Box sx={main.titleInfo}>
            <Typography variant="h5">
              {isSoleTrader ? titleFinancialCondition.fop : titleFinancialCondition.legal}
            </Typography>
            <FormAutocomplete
              name="prevDate"
              options={prevDateOptions}
              inputProps={{
                placeholder: 'Оберіть дату',
              }}
              sx={{ ...main.tableInput, paddingBottom: 0 }}
            />
            {!isSoleTrader && <Typography variant="h5">(сума у тис. грн.)</Typography>}
          </Box>
          {isSoleTrader ? <BlockFinancialSoleTrader /> : <BlockFinancialLegalResident />}
          <Box sx={{ justifyContent: 'center', display: 'flex', width: '100%' }}>
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              Продовжити
            </Button>
          </Box>
        </Box>
        <SetDirtyModalValues
          saveValues={form.handleSubmit(onSubmit(isDirty, true), onInvalidForm)}
        />
      </Box>
      {isSubmitting && <FullPageLoader />}
    </FormProvider>
  );
}
